import {
  BlockVideoStreamInput,
  CameraCapability,
  PresetInput,
  RecordingType,
  VideoStreamAccessType,
  useBlockVideoStreamMutation,
  useChangeVideoTourStateMutation,
  useEnableVideoCameraAutoFocusMutation,
  useEnableVideoCameraAutoIrisMutation,
  useFocusVideoCameraMutation,
  useIrisVideoCameraMutation,
  useSaveVideoCameraPresetMutation,
  useUnblockVideoStreamMutation,
  useUseVideoCameraCapabilityMutation,
  useVideoCameraGoToPresetMutation,
  useVideoCameraPublicFeedMutation,
  useVideoCameraRecordingMutation,
} from '@kinetic-ui/data-layer';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGraphLoading } from 'kineticUtils/hooks';
import { BlockRequest } from './BlockPublicStreamPopover';

export const useCameraOverlayMutations = (cameraId: string, refetchData?: () => void) => {
  const { t } = useTranslation(['video', 'common']);
  const [videoCameraPublicFeedMutation] = useVideoCameraPublicFeedMutation();
  const [doVideoCameraCapability, capabilityResult] = useUseVideoCameraCapabilityMutation();

  const [enableCameraAutoIris] = useEnableVideoCameraAutoIrisMutation();
  const [enableCameraAutoFocus] = useEnableVideoCameraAutoFocusMutation();
  const [focusVideoCamera] = useFocusVideoCameraMutation();
  const [irisVideoCamera] = useIrisVideoCameraMutation();
  const [cameraRecording, recordingResult] = useVideoCameraRecordingMutation();
  const [changeTourState] = useChangeVideoTourStateMutation();

  const capabilitiesMessages = {
    success: t('jobRequestSuccess', { request: t('capability') }),
    error: t('jobRequestFailure', { request: t('capability') }),
  };

  const savePresetResultMessages = useMemo(() => {
    return {
      success: t('common:successfulSave', { name: t('video:preset') }),
      error: t('common:errorOnSave', { name: t('video:preset') }),
    };
  }, [t]);

  const [cameraGoToPreset] = useVideoCameraGoToPresetMutation();
  const [savePresetMutation, savePresetResult] = useSaveVideoCameraPresetMutation();
  useGraphLoading({
    result: savePresetResult,
    name: 'savePreset',
    messages: savePresetResultMessages,
  });

  useGraphLoading({
    result: recordingResult,
    name: 'saveCameraRecording',
    messages: {
      error: t('common:errorStartRecording', { message: t(recordingResult.error?.message) }),
    },
  });

  const toggleIsPublic = (cameraId: string, streamManagerId: string, enable: boolean) => {
    videoCameraPublicFeedMutation({
      variables: {
        cameraPublicFeedInput: {
          cameraId,
          streamManagerId,
          connect: enable,
        },
      },
    });
  };

  const [blockVideoStreamMutation, blockVideoStreamResult] = useBlockVideoStreamMutation();
  const [unblockVideoStreamMutation, unblockVideoStreamResult] = useUnblockVideoStreamMutation();

  const blockResultMessages = useMemo(() => {
    return {
      success: t('video:blockSuccess'),
      error: t('video:blockFailure'),
    };
  }, [t]);
  const unblockResultMessages = useMemo(() => {
    return {
      success: t('video:unblockSuccess'),
      error: t('video:unblockFailure'),
    };
  }, [t]);

  useGraphLoading({
    result: blockVideoStreamResult,
    name: 'blockVideoStream',
    messages: blockResultMessages,
  });
  useGraphLoading({
    result: unblockVideoStreamResult,
    name: 'unblockVideoStream',
    messages: unblockResultMessages,
  });

  const blockPublicKvdsStream = (cameraId: string, request: BlockRequest) => {
    const input: BlockVideoStreamInput = {
      cameraId,
      ...(request.mode === 'duration' ? { duration: request.duration.trim() } : {}),
      ...(request.mode === 'until' ? { endsAt: request.until } : {}),
    };
    return blockVideoStreamMutation({ variables: { input } })
      .then(({ data }) => {
        refetchData?.();
        return data?.blockVideoStream ?? null;
      })
      .catch((mutationError) => {
        console.error('blockVideoStream failed', { cameraId, input, mutationError });
        throw mutationError;
      });
  };

  const unblockPublicKvdsStream = (cameraId: string) => {
    return unblockVideoStreamMutation({ variables: { input: { cameraId } } })
      .then(({ data }) => {
        refetchData?.();
        return data?.unblockVideoStream ?? false;
      })
      .catch((mutationError) => {
        console.error('unblockVideoStream failed', { cameraId, mutationError });
        throw mutationError;
      });
  };

  const doCameraCapability = (cameraId: string, capability: CameraCapability, option: string) =>
    doVideoCameraCapability({ variables: { cameraId, capability, option } });

  useGraphLoading({
    name: 'VideoCameraCapability',
    result: capabilityResult,
    messages: capabilitiesMessages,
  });

  const enableAutoFocus = (cameraId: string, enable: boolean) =>
    enableCameraAutoFocus({ variables: { cameraAutoFocusInput: { cameraId, enable } } });

  const enableAutoIris = (cameraId: string, enable: boolean) =>
    enableCameraAutoIris({ variables: { cameraAutoIrisInput: { cameraId, enable } } });

  const irisCamera = (cameraId: string, irisSpeed: number) =>
    irisVideoCamera({ variables: { cameraIrisInput: { cameraId, irisSpeed } } });

  const focusCamera = (cameraId: string, focusSpeed: number) =>
    focusVideoCamera({ variables: { cameraFocusInput: { cameraId, focusSpeed } } });

  const gotoPreset = (presetId: string) => {
    cameraGoToPreset({
      variables: {
        cameraId,
        presetId,
      },
    });
  };

  const savePreset = useCallback(
    (presetName: string, isSystem: boolean, presetId?: string) => {
      const preset: PresetInput = {
        id: presetId,
        cameraId,
        name: presetName,
        isSystem,
      };

      savePresetMutation({
        variables: {
          input: {
            ...preset,
          },
        },
      }).then(() => {
        refetchData?.();
      });
    },
    [cameraId, refetchData, savePresetMutation]
  );

  const toggleRecording = (
    cameraId: string,
    streamRecorderId: string,
    streamAccessType: VideoStreamAccessType,
    start: boolean,
    recordingName: string,
    recordingType?: RecordingType
  ) => {
    cameraRecording({
      variables: {
        cameraId,
        streamRecorderId,
        streamAccessType,
        start,
        recordingType,
        recordingName,
      },
    }).then(() => refetchData?.());
  };

  const toggleIsPaused = (tourId: string, paused: boolean) =>
    changeTourState({ variables: { tourId, paused } });

  return {
    toggleIsPublic,
    blockPublicKvdsStream,
    unblockPublicKvdsStream,
    doCameraCapability,
    enableAutoFocus,
    enableAutoIris,
    irisCamera,
    focusCamera,
    gotoPreset,
    savePreset,
    toggleRecording,
    toggleIsPaused,
  };
};
